'use client';

import React, { useEffect, useMemo, useState } from 'react';
import {
  Calculator,
  Phone,
  Mic,
  BrainCircuit,
  Server,
  Wallet,
  TrendingUp,
  Clock3,
} from 'lucide-react';

const fmtPln = (value: number) =>
  new Intl.NumberFormat('pl-PL', {
    style: 'currency',
    currency: 'PLN',
    maximumFractionDigits: 0,
  }).format(Math.max(0, value));

const fmtNum = (value: number) => new Intl.NumberFormat('pl-PL').format(Math.round(value));
const fmtDecimal = (value: number) =>
  new Intl.NumberFormat('pl-PL', {
    minimumFractionDigits: value % 1 === 0 ? 0 : 2,
    maximumFractionDigits: 3,
  }).format(value);

type PricingData = {
  generatedAt: string;
  warnings: string[];
  sources: {
    twilio: string;
    elevenlabs: string;
    gemini: string;
    hetzner: string;
    fx: string;
  };
  rates: {
    twilioUsdPerMin: number;
    elevenUsdPerMin: number;
    geminiUsdPerMin: number;
    geminiInputAudioUsdPer1M: number;
    geminiOutputAudioUsdPer1M: number;
    geminiInputTokensPerMin: number;
    geminiOutputTokensPerMin: number;
    hetznerEurMonthly: number;
    usdPln: number;
    eurPln: number;
    servicePln: number;
    employeeMonthlyPln: number;
  };
  selection: {
    twilio: string;
    elevenlabs: string;
    gemini: string;
    hetzner: string;
    elevenPlan: string;
    geminiModel: string;
    fxDate: string | null;
  };
};

const FALLBACK_PRICING: PricingData = {
  generatedAt: new Date().toISOString(),
  warnings: ['Użyto fallback cen'],
  sources: {
    twilio: 'https://www.twilio.com/en-us/voice/pricing/pl',
    elevenlabs: 'https://elevenlabs.io/pricing',
    gemini: 'https://ai.google.dev/gemini-api/docs/pricing',
    hetzner: 'https://www.hetzner.com/cloud/',
    fx: 'https://api.nbp.pl',
  },
  rates: {
    twilioUsdPerMin: 0.0715,
    elevenUsdPerMin: 0.09,
    geminiUsdPerMin: 0.0125,
    geminiInputAudioUsdPer1M: 1,
    geminiOutputAudioUsdPer1M: 0.1,
    geminiInputTokensPerMin: 12000,
    geminiOutputTokensPerMin: 5000,
    hetznerEurMonthly: 4.49,
    usdPln: 4,
    eurPln: 4.3,
    servicePln: 1500,
    employeeMonthlyPln: 8525,
  },
  selection: {
    twilio: 'mobile_make',
    elevenlabs: 'pro_monthly_calls_per_minute',
    gemini: 'audio_token_based',
    hetzner: 'cpx21_monthly',
    elevenPlan: 'pro',
    geminiModel: 'gemini-2.5-flash',
    fxDate: null,
  },
};

export type CostMetrics = {
  monthlySavings: number;
  yearlySavings: number;
  aiMonthly: number;
  employeeMonthly: number;
};

const EMPLOYEE_HOURS_PER_MONTH = 168;
const EMPLOYEE_MINUTES_PER_MONTH = EMPLOYEE_HOURS_PER_MONTH * 60;

const StaticValue: React.FC<{
  label: string;
  value: number;
  suffix?: string;
}> = ({ label, value, suffix }) => (
  <div className="rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3">
    <p className="text-sm font-semibold text-slate-200">{label}</p>
    <p className="mt-1 text-xl font-black text-white">
      {fmtDecimal(value)} {suffix ? <span className="text-sm font-semibold text-slate-400">{suffix}</span> : null}
    </p>
  </div>
);

const SliderInput: React.FC<{
  label: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  suffix?: string;
}> = ({ label, value, onChange, min = 0, max = 100, step = 1, suffix }) => (
  <label className="block rounded-xl border border-slate-700 bg-slate-900/50 px-4 py-3 transition focus-within:border-cyan-400/70 focus-within:bg-slate-900/70">
    <div className="mb-2 flex items-center justify-between gap-3">
      <span className="text-sm font-semibold text-slate-200">{label}</span>
      <span className="inline-flex min-w-[84px] items-baseline justify-center gap-1 whitespace-nowrap rounded-md bg-slate-800 px-2 py-1 text-xs font-bold text-cyan-200">
        <span>{fmtDecimal(value)}</span>
        {suffix ? <span>{suffix}</span> : null}
      </span>
    </div>
    <div className="relative pt-1">
      <input
        type="range"
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={(e) => onChange(Number(e.target.value))}
        className="slider-clean h-2 w-full cursor-pointer rounded-lg"
      />
    </div>
  </label>
);

interface ROICalculatorProps {
  onMetricsChange?: (metrics: CostMetrics) => void;
}

const ROICalculator: React.FC<ROICalculatorProps> = ({ onMetricsChange }) => {
  const [pricing, setPricing] = useState<PricingData>(FALLBACK_PRICING);
  const [callsPerDay, setCallsPerDay] = useState(100);
  const [avgMinutes, setAvgMinutes] = useState(1);
  const [daysPerMonth, setDaysPerMonth] = useState(30);

  useEffect(() => {
    const controller = new AbortController();
    fetch(`/pricing-data.json?t=${Date.now()}`, { signal: controller.signal })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error(`HTTP ${res.status}`))))
      .then((data: PricingData) => setPricing(data))
      .catch(() => {
        // Zostawiamy fallbacki, jeśli nie uda się pobrać świeżych danych.
      });
    return () => controller.abort();
  }, []);

  const usdPln = pricing.rates.usdPln;
  const eurPln = pricing.rates.eurPln;
  const twilioUsdPerMin = pricing.rates.twilioUsdPerMin;
  const elevenUsdPerMin = pricing.rates.elevenUsdPerMin;
  const geminiUsdPerMin = pricing.rates.geminiUsdPerMin;
  const hetznerEurMonthly = pricing.rates.hetznerEurMonthly;
  const servicePln = pricing.rates.servicePln;
  const employeeMonthly = pricing.rates.employeeMonthlyPln;

  const result = useMemo(() => {
    const minutesMonthly = callsPerDay * avgMinutes * daysPerMonth;
    const employeeFte = Math.max(1, Math.ceil(minutesMonthly / EMPLOYEE_MINUTES_PER_MONTH));
    const employeeMonthlyDynamic = employeeFte * employeeMonthly;

    const twilioPln = minutesMonthly * twilioUsdPerMin * usdPln;
    const elevenPln = minutesMonthly * elevenUsdPerMin * usdPln;
    const geminiPln = minutesMonthly * geminiUsdPerMin * usdPln;
    const hostingPln = hetznerEurMonthly * eurPln;

    const aiMonthly = twilioPln + elevenPln + geminiPln + hostingPln + servicePln;
    const monthlySavings = employeeMonthlyDynamic - aiMonthly;
    const yearlySavings = monthlySavings * 12;

    return {
      minutesMonthly,
      employeeFte,
      employeeMonthlyDynamic,
      twilioPln,
      elevenPln,
      geminiPln,
      hostingPln,
      aiMonthly,
      monthlySavings,
      yearlySavings,
    };
  }, [
    callsPerDay,
    avgMinutes,
    daysPerMonth,
    twilioUsdPerMin,
    elevenUsdPerMin,
    geminiUsdPerMin,
    hetznerEurMonthly,
    usdPln,
    eurPln,
    servicePln,
    employeeMonthly,
  ]);

  useEffect(() => {
    onMetricsChange?.({
      monthlySavings: result.monthlySavings,
      yearlySavings: result.yearlySavings,
      aiMonthly: result.aiMonthly,
      employeeMonthly: result.employeeMonthlyDynamic,
    });
  }, [onMetricsChange, result.monthlySavings, result.yearlySavings, result.aiMonthly, result.employeeMonthlyDynamic]);

  return (
    <section id="roi" className="relative overflow-hidden bg-slate-950 py-24 text-white">
      <div className="absolute left-1/2 top-0 h-96 w-[50rem] -translate-x-1/2 rounded-full bg-cyan-500/15 blur-3xl" />
      <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-cyan-400/10 blur-3xl" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <div className="mb-4 inline-flex items-center rounded-full border border-cyan-400/40 bg-cyan-500/10 px-4 py-2 text-sm font-semibold text-cyan-200">
            <Calculator className="mr-2 h-4 w-4" />
            Asystent Sprzedaży AI - Kalkulator Oferty
          </div>
          <h2 className="text-3xl font-black leading-tight sm:text-5xl">
            Zobacz ile realnie oszczędzasz
            <br className="hidden sm:block" />
            na korzyść AI Agenta
          </h2>
          <p className="mx-auto mt-4 max-w-3xl text-lg text-slate-300">
            System AI odbiera połączenia, rozmawia z klientami, umawia wizyty i przekazuje informacje
            automatycznie 24/7. Wpisz swoje dane i od razu zobacz koszt infrastruktury oraz oszczędność
            vs pracownik etatowy.
          </p>
        </div>

        <div className="grid gap-8 xl:grid-cols-5">
          <div className="space-y-6 rounded-3xl border border-slate-700/60 bg-slate-900/70 p-6 backdrop-blur xl:col-span-3">
            <div>
              <h3 className="text-xl font-bold text-cyan-200">Założenia operacyjne</h3>
              <p className="mt-1 text-sm text-slate-400">Wpisz swoje wartości i przelicz ofertę.</p>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <SliderInput label="Połączenia dziennie" value={callsPerDay} onChange={setCallsPerDay} min={10} max={100} step={5} />
              <SliderInput label="Średni czas rozmowy" value={avgMinutes} onChange={setAvgMinutes} min={0.5} max={6} step={0.5} suffix="min" />
              <SliderInput label="Dni w miesiącu" value={daysPerMonth} onChange={setDaysPerMonth} min={20} max={31} step={1} />
            </div>
          </div>

          <div className="space-y-4 rounded-3xl border border-cyan-300/30 bg-slate-900/80 p-6 shadow-2xl shadow-cyan-500/10 xl:col-span-2">
            <div className="rounded-2xl border border-slate-700 bg-slate-950/80 p-4">
              <p className="text-xs uppercase tracking-wider text-slate-400">Łączna liczba minut / miesiąc</p>
              <p className="mt-2 text-4xl font-black text-cyan-300">{fmtNum(result.minutesMonthly)}</p>
            </div>

            <div className="grid gap-3">
              <div className="flex items-center justify-between rounded-xl bg-slate-800/70 p-3">
                <p className="flex items-center gap-2 text-sm text-slate-200"><Phone className="h-4 w-4 text-cyan-300" /> Telefonia</p>
                <p className="font-semibold">{fmtPln(result.twilioPln)}</p>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-800/70 p-3">
                <p className="flex items-center gap-2 text-sm text-slate-200"><Mic className="h-4 w-4 text-cyan-300" /> Synteza i obsługa głosu</p>
                <p className="font-semibold">{fmtPln(result.elevenPln)}</p>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-800/70 p-3">
                <p className="flex items-center gap-2 text-sm text-slate-200"><BrainCircuit className="h-4 w-4 text-cyan-300" /> Model językowy AI</p>
                <p className="font-semibold">{fmtPln(result.geminiPln)}</p>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-800/70 p-3">
                <p className="flex items-center gap-2 text-sm text-slate-200"><Server className="h-4 w-4 text-cyan-300" /> Hosting infrastruktury</p>
                <p className="font-semibold">{fmtPln(result.hostingPln)}</p>
              </div>
              <div className="flex items-center justify-between rounded-xl bg-slate-800/70 p-3">
                <p className="flex items-center gap-2 text-sm text-slate-200"><Wallet className="h-4 w-4 text-cyan-300" /> Obsługa i serwis</p>
                <p className="font-semibold">{fmtPln(servicePln)}</p>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-cyan-400/50 bg-cyan-500/10 p-4">
                <p className="text-sm font-semibold uppercase tracking-wide text-cyan-200">Koszt AI Agent / miesiąc</p>
                <p className="mt-2 text-3xl font-black text-white">{fmtPln(result.aiMonthly)}</p>
              </div>

              <div className="rounded-2xl border border-cyan-400/40 bg-cyan-500/10 p-4">
                <p className="text-sm font-semibold uppercase tracking-wide text-cyan-200">
                  Koszt pracownika ({result.employeeFte} etat{result.employeeFte > 1 ? 'y' : ''})
                </p>
                <p className="mt-2 text-3xl font-black text-white">{fmtPln(result.employeeMonthlyDynamic)}</p>
                <p className="mt-1 text-xs text-cyan-100/90">
                  Limit: 1 etat = {EMPLOYEE_HOURS_PER_MONTH}h ({fmtNum(EMPLOYEE_MINUTES_PER_MONTH)} min) / miesiąc
                </p>
              </div>
            </div>

            <div className="rounded-2xl border border-cyan-400/40 bg-cyan-500/10 p-4">
              <p className="flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-cyan-200">
                <TrendingUp className="h-4 w-4" /> Oszczędność miesięczna vs etat
              </p>
              <p className="mt-2 text-3xl font-black text-cyan-100">
                {result.monthlySavings >= 0 ? '+' : '-'}{fmtPln(Math.abs(result.monthlySavings))}
              </p>
              <p className="mt-1 text-sm text-slate-300">Rocznie: {result.yearlySavings >= 0 ? '+' : '-'}{fmtPln(Math.abs(result.yearlySavings))}</p>
            </div>

            <div className="rounded-2xl border border-slate-700 bg-slate-950/60 p-4 text-sm text-slate-300">
              <p className="mb-1 flex items-center gap-2 font-semibold text-slate-100"><Clock3 className="h-4 w-4 text-cyan-300" /> Przewaga operacyjna</p>
              <p>AI Agent działa 24/7 bez urlopów i L4, podczas gdy etat to zwykle ok. 8h dziennie.</p>
            </div>
          </div>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-700 bg-slate-900/70 p-4 text-xs text-slate-400">
          Ostatnia automatyczna aktualizacja cen: {new Date(pricing.generatedAt).toLocaleString('pl-PL')}. Twilio:
          {` ${pricing.selection.twilio}`}, ElevenLabs plan: {` ${pricing.selection.elevenPlan}`}, Gemini model:
          {` ${pricing.selection.geminiModel}`}. Kurs NBP: {pricing.selection.fxDate ?? 'fallback'}.
        </div>
      </div>
    </section>
  );
};

export default ROICalculator;
